import React from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronDown, ChevronRight } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { TruncateText } from '../../lib/truncate-text.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import { itemVariants } from '../../lib/variants.jsx';

export interface SidebarItemProps extends Omit<
  React.ComponentProps<'button'>,
  'children'
> {
  text: string;
  variant?: 'primary' | 'secondary';
  icon?: React.ReactNode;
  /**
   * shows the chevron that toggles nested items
   */
  expandable?: boolean;
  open?: boolean;
  selected?: boolean;
  /**
   * called on chevron click, falls back to `onClick` when not provided.
   * The chevron stays clickable when the item is disabled, so nested items remain reachable.
   */
  onChevronClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  endAdornment?: React.ReactNode;
}

const SidebarItem = ({
  text,
  icon,
  expandable = false,
  open = false,
  selected = false,
  disabled = false,
  variant = 'primary',
  onChevronClick,
  endAdornment,
  className,
  ...props
}: SidebarItemProps) => {
  const { t } = useTranslation('@mint-lib/tw-components');

  const handleChevronClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    (onChevronClick ?? props.onClick)?.(e);
  };

  return (
    <div
      data-slot="sidebar-item"
      data-selected={selected}
      data-disabled={disabled}
      data-expandable={expandable}
      className={cn(itemVariants({ sidebarItem: variant, className }))}
    >
      {expandable && (
        <button
          type="button"
          data-slot="sidebar-item-chevron"
          aria-label={open ? t('Collapse') : t('Expand')}
          aria-expanded={open}
          onClick={handleChevronClick}
          data-dd-action-name="Sidebar item: toggle nested items"
          className="flex shrink-0 cursor-pointer fill-text-secondary focus-visible:outline-2 focus-visible:outline-action-focusBorder [&>svg]:size-4"
        >
          {open ? <ChevronDown /> : <ChevronRight />}
        </button>
      )}
      <button
        type="button"
        data-slot="sidebar-item-button"
        disabled={disabled}
        aria-current={selected ? 'page' : undefined}
        className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 text-left focus-visible:outline-2 focus-visible:outline-action-focusBorder disabled:pointer-events-none disabled:fill-text-disabled disabled:text-text-disabled"
        {...props}
      >
        {icon && <span className="flex shrink-0 [&>svg]:size-4">{icon}</span>}
        <TruncateText>
          <span
            className={typographyVariants({
              variant: variant === 'primary' ? 'bodyShort02' : 'bodyShort01',
              className: 'block w-full min-w-0',
            })}
          >
            {text}
          </span>
        </TruncateText>
        {endAdornment && (
          <span className="ml-auto inline-flex shrink-0">{endAdornment}</span>
        )}
      </button>
    </div>
  );
};

export default SidebarItem;
